import { isDfsZonenEffective } from "@client/lib/ctrAltitude";

export const BUNDESLAENDER = [
  "Baden-Württemberg",
  "Bayern",
  "Berlin",
  "Brandenburg",
  "Bremen",
  "Hamburg",
  "Hessen",
  "Mecklenburg-Vorpommern",
  "Niedersachsen",
  "Nordrhein-Westfalen",
  "Rheinland-Pfalz",
  "Saarland",
  "Sachsen",
  "Sachsen-Anhalt",
  "Schleswig-Holstein",
  "Thüringen",
] as const;

export type Bundesland = (typeof BUNDESLAENDER)[number];

export interface Paragraph21hRule {
  id: string;
  label: string;
  auflagen: string;
  paragraph?: string;
  tatbestand?: string;
  layerMatch: string[];
  /**
   * Kann diese Zonenkategorie realistisch durch eine geografische Landes-
   * Allgemeinerlaubnis (§ 21h) abgedeckt werden? Nur Kategorien mit Landes-
   * zuständigkeit (Wohngrundstücke, Bundesfern-/Wasserstraßen, Bahn) = true.
   * Luftsicherheits-/zustimmungspflichtige Zonen (Flugplätze/HEMS, Flughäfen/
   * CTR, Industrie-/Energieanlagen, Behörden) = false (DFS/ATC, Betreiber bzw.
   * die jeweilige Stelle geben frei, NICHT eine Landes-AV).
   */
  avEligible: boolean;
}

export const PARAGRAPH_21H_RULES: Paragraph21hRule[] = [
  {
    id: "flugplaetze",
    label: "Flugplätze / HEMS",
    auflagen:
      "Flug näher als 1,5 km nur bis max. 30 m über Grund. Aufstiegsorte für Hubschrauber im Rettungsdienst (HEMS) freihalten.",
    layerMatch: ["flugplätze", "flugplatz", "hems", "hubschrauber"],
    avEligible: false,
  },
  {
    id: "flughaefen",
    label: "Flughäfen / Kontrollzonen",
    auflagen:
      (isDfsZonenEffective()
        ? "4-Zonen-Modell (NfL 2026-1-3960, hebt NfL 2023-1-2705 auf)"
        : "Neues 4-Zonen-Modell (NfL 2026-1-3960, gültig ab 06.08.2026, hebt NfL 2023-1-2705 auf)") + " – gilt nur in den CTR der 15 Flughäfen mit DFS-Flugplatzkontrolle. Zone 1 (Flughafenbereich + 1-km/5-km-Bereich): immer individuelle FVK-Freigabe der DFS; offene Kategorie zusätzlich Genehmigung der Landesluftfahrtbehörde (§ 21i). Zone 2 (bis 4 km): max. 25 m über Grund/Flugplatzhöhe (niedrigerer Wert); Zone 3 (bis 6 km): max. 45 m (niedrigerer Wert); Zone 4 (Rest-CTR): 100 m über Flugplatzhöhe oder 50 m über Grund (höherer Wert). Zonen-Geometrie je Flughafen: AIP GERMANY/dipul.",
    layerMatch: ["flughäfen", "flughafen", "kontrollzon", "ctr"],
    avEligible: false,
  },
  {
    id: "industrieanlagen",
    label: "Industrieanlagen",
    auflagen:
      "Flug näher als 100 m nur mit A1/A2-Kompetenz; kein Überflug ohne Zustimmung des Betreibers.",
    layerMatch: ["industrieanlagen", "industrie"],
    avEligible: false,
  },
  {
    id: "energieanlagen",
    label: "Energieanlagen",
    paragraph: "§ 21h (3) Nr. 3 LuftVO",
    tatbestand:
      "Über und innerhalb eines seitlichen Abstands von 100 m zu Anlagen der zentralen Energieerzeugung und -verteilung ist der Betrieb nur mit ausdrücklicher Zustimmung der zuständigen Stelle oder des Betreibers zulässig.",
    auflagen:
      "Außerhalb des 100-m-Abstands greift diese Zustimmungspflicht nach § 21h Abs. 3 Nr. 3 nicht; andere Regeln können gelten. Die 100 m messen sich ab der Anlagenbegrenzung. Ein Kartentreffer allein belegt weder die rechtliche Einstufung als zentrale Anlage noch deren genaue Begrenzung.",
    layerMatch: ["stromleitung", "umspannwerk", "kraftwerk", "windkraft", "energie"],
    avEligible: false,
  },
  {
    id: "behoerden",
    label: "Verfassungsorgane / Sicherheitsbehörden",
    auflagen:
      "Mindestabstand 100 m zu obersten Bundes-/Landesbehörden, Polizei- und Sicherheitsbehörden, JVA, diplomatischen Vertretungen und internationalen Organisationen.",
    layerMatch: [
      "behörd",
      "polizei",
      "sicherheitsbehörden",
      "jva",
      "maßregelvollzug",
      "diplomatische",
      "internationale organisationen",
      "verfassungsorgan",
    ],
    avEligible: false,
  },
  {
    id: "bundesfernstrassen",
    label: "Bundesfernstraßen",
    auflagen:
      "Überflug von Bundesautobahnen und Bundesstraßen nur zügig und in mindestens 30 m Höhe.",
    layerMatch: ["bundesautobahn", "autobahn", "bundesstraße", "bundesstrasse"],
    avEligible: true,
  },
  {
    id: "bundeswasserstrassen",
    label: "Bundeswasserstraßen",
    auflagen:
      "Mindestabstand 100 m zu Bundeswasserstraßen; zusätzlich ausreichend Abstand zu fahrenden Wasserfahrzeugen und Schifffahrtsanlagen.",
    layerMatch: ["binnenwasserstraße", "seewasserstraße", "wasserstraße", "wasserstrasse", "schifffahrtsanlagen"],
    avEligible: true,
  },
  {
    id: "bahnanlagen",
    label: "Bahnanlagen",
    auflagen:
      "Mindestabstand 100 m zu Bahnanlagen; Überflug von Oberleitungen in mindestens 5 m Höhe über der Leitung.",
    layerMatch: ["bahnanlagen", "bahn"],
    avEligible: true,
  },
  {
    id: "naturschutzgebiete",
    label: "Naturschutz- / Natura-2000-Gebiete",
    auflagen:
      "Betrieb über Naturschutzgebieten, Nationalparks und Natura-2000-Gebieten (FFH-/Vogelschutzgebieten) ist untersagt und nur mit Zustimmung der zuständigen Naturschutzbehörde zulässig. Nicht durch die luftrechtliche Landes-Allgemeinerlaubnis (§ 21h) gedeckt. Störungen von Tieren (v. a. Brut-/Rast-/Setzzeiten) vermeiden.",
    layerMatch: ["naturschutzgebiet", "naturschutz", "vogelschutz", "nationalpark", "ffh", "natura"],
    avEligible: false,
  },
  {
    id: "wohngrundstuecke",
    label: "Wohngrundstücke",
    auflagen:
      "Überflug von Wohngrundstücken nur mit A1/A2-Kompetenz und unter Beachtung der Privatsphäre (keine Aufnahmen ohne Zustimmung).",
    layerMatch: ["wohngrundstück", "wohngrundstuecke", "wohn"],
    avEligible: true,
  },
];

export function find21hRuleForLayer(layerLabel?: string | null): Paragraph21hRule | null {
  const label = (layerLabel || "").toLowerCase();
  if (!label) return null;
  for (const rule of PARAGRAPH_21H_RULES) {
    if (rule.layerMatch.some((kw) => label.includes(kw))) return rule;
  }
  return null;
}

/**
 * Kann der gegebene Geozonen-Layer durch eine geografische Landes-
 * Allgemeinerlaubnis (§ 21h) abgedeckt werden? Nur wenn eine passende Regel
 * existiert UND diese `avEligible` ist (siehe Feld-Doku).
 */
export function is21hLayerAvEligible(layerLabel?: string | null): boolean {
  const rule = find21hRuleForLayer(layerLabel);
  return rule !== null && rule.avEligible === true;
}

export type AvCoverageStatus = "approved" | "limited" | "none";

export interface AvCoverageResult {
  /** Suggested restriction status, or null when no assessment applies (no geozones / not DE). */
  suggestedStatus: AvCoverageStatus | null;
  /** True only when a valid permit covers all selected §21h-fähige zones and no non-covered zone is present. */
  covered: boolean;
  /** Reason the coverage is incomplete, for UI messaging. */
  reason: "ok" | "no-a2" | "no-bundesland" | "missing-permit" | "expired-permit" | "non-21h-zone" | "none-selected";
}

/**
 * Pure decision engine for the web planning runtime: given the pilot's A2/permit
 * state for the detected Bundesland and the geozone types selected at the
 * location, derive whether the flight is covered by a § 21h Allgemeinerlaubnis.
 * No mutual recognition — the permit must match the detected Bundesland.
 */
export function assessAvCoverage(opts: {
  inGermany: boolean;
  detectedBundesland: string | null;
  hasA2: boolean;
  erlaubnis?: GeoAllgemeinerlaubnis | null;
  coveredGeozoneCount: number;
  nonCoveredGeozoneCount: number;
}): AvCoverageResult {
  const { inGermany, detectedBundesland, hasA2, erlaubnis } = opts;
  const anySelected = opts.coveredGeozoneCount > 0 || opts.nonCoveredGeozoneCount > 0;

  if (!inGermany || !anySelected) {
    return { suggestedStatus: null, covered: false, reason: "none-selected" };
  }

  const expired = erlaubnis ? isErlaubnisExpired(erlaubnis) : false;
  const hasValidErlaubnis = hasA2 && !!erlaubnis && !expired;

  const covered =
    hasValidErlaubnis &&
    opts.coveredGeozoneCount > 0 &&
    opts.nonCoveredGeozoneCount === 0;

  if (covered) {
    return { suggestedStatus: "approved", covered: true, reason: "ok" };
  }

  let reason: AvCoverageResult["reason"] = "missing-permit";
  if (opts.nonCoveredGeozoneCount > 0) reason = "non-21h-zone";
  else if (!hasA2) reason = "no-a2";
  else if (!detectedBundesland) reason = "no-bundesland";
  else if (!erlaubnis) reason = "missing-permit";
  else if (expired) reason = "expired-permit";

  return { suggestedStatus: "limited", covered: false, reason };
}

export interface BundeslandAvInfo {
  behorde: string;
  antragUrl: string;
  infoUrl?: string;
}

export const DIPUL_ANTRAG_URL =
  "https://dipul.de/homepage/de/antraege-behoerdendienste/einflug-in-geografische-gebiete/";

export const BUNDESLAND_AV_INFO: Record<string, BundeslandAvInfo> = {
  "Baden-Württemberg": {
    behorde: "Regierungspräsidium Stuttgart",
    antragUrl: DIPUL_ANTRAG_URL,
    infoUrl: "https://rp.baden-wuerttemberg.de/rps/abt4/ref46/drohnen/",
  },
  Bayern: {
    behorde: "Regierung von Oberbayern - Luftamt Südbayern",
    antragUrl: DIPUL_ANTRAG_URL,
    infoUrl: "https://www.regierung.oberbayern.bayern.de/aufgaben/wirtschaft/luftverkehr/",
  },
  Berlin: {
    behorde: "Senatsverwaltung für Umwelt, Mobilität, Verbraucher- und Klimaschutz",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Brandenburg: {
    behorde: "Ministerium für Infrastruktur und Landesplanung",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Bremen: {
    behorde: "Senator für Wirtschaft, Arbeit und Häfen",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Hamburg: {
    behorde: "Behörde für Wirtschaft und Innovation",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Hessen: {
    behorde: "Regierungspräsidium Kassel",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  "Mecklenburg-Vorpommern": {
    behorde:
      "Landesamt für zentrale Aufgaben und Technik der Polizei, Brand- und Katastrophenschutz",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Niedersachsen: {
    behorde: "Niedersächsische Landesbehörde für Straßenbau und Verkehr",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  "Nordrhein-Westfalen": {
    behorde: "Bezirksregierung Düsseldorf",
    antragUrl: DIPUL_ANTRAG_URL,
    infoUrl: "https://www.brd.nrw.de/themen/verkehr/luftverkehr",
  },
  "Rheinland-Pfalz": {
    behorde: "Landesbetrieb Mobilität Rheinland-Pfalz",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Saarland: {
    behorde: "Ministerium für Wirtschaft, Innovation, Digitales und Energie",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Sachsen: {
    behorde: "Landesdirektion Sachsen",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  "Sachsen-Anhalt": {
    behorde: "Landesverwaltungsamt Sachsen-Anhalt",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  "Schleswig-Holstein": {
    behorde: "Landesbetrieb Straßenbau und Verkehr Schleswig-Holstein",
    antragUrl: DIPUL_ANTRAG_URL,
  },
  Thüringen: {
    behorde: "Thüringer Landesamt für Bau und Verkehr",
    antragUrl: DIPUL_ANTRAG_URL,
  },
};

export interface DocumentRef {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  dataBase64: string;
}

export interface GeoAllgemeinerlaubnis {
  bundesland: Bundesland;
  expirationDate?: string;
  document?: DocumentRef | null;
}

export function parseGermanDate(raw?: string | null): Date | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const build = (y: number, mIdx: number, d: number): Date | null => {
    if (y < 1900 || y > 2200 || mIdx < 0 || mIdx > 11 || d < 1 || d > 31) return null;
    const date = new Date(y, mIdx, d);
    if (
      isNaN(date.getTime()) ||
      date.getFullYear() !== y ||
      date.getMonth() !== mIdx ||
      date.getDate() !== d
    )
      return null;
    date.setHours(0, 0, 0, 0);
    return date;
  };
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return build(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    return build(year, Number(m[2]) - 1, Number(m[1]));
  }
  return null;
}

export function isErlaubnisExpired(e: GeoAllgemeinerlaubnis, now: Date = new Date()): boolean {
  const exp = parseGermanDate(e.expirationDate);
  if (!exp) return false;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return exp.getTime() < today.getTime();
}

export function isErlaubnisValid(e: GeoAllgemeinerlaubnis, now: Date = new Date()): boolean {
  return !isErlaubnisExpired(e, now);
}

export function migrateAllgemeinerlaubnis(raw: any): GeoAllgemeinerlaubnis[] {
  if (Array.isArray(raw)) {
    return raw
      .filter((e) => e && typeof e.bundesland === "string")
      .map((e) => ({
        bundesland: e.bundesland as Bundesland,
        expirationDate: e.expirationDate || undefined,
        document: e.document ?? null,
      }));
  }
  if (raw && typeof raw === "object" && Array.isArray(raw.bundeslaender)) {
    const seen = new Set<string>();
    const result: GeoAllgemeinerlaubnis[] = [];
    for (const bl of raw.bundeslaender) {
      if (typeof bl !== "string" || seen.has(bl)) continue;
      seen.add(bl);
      result.push({ bundesland: bl as Bundesland, expirationDate: undefined, document: null });
    }
    return result;
  }
  return [];
}
